import { Loader2, Search } from 'lucide-react';
import { type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
interface SearchFilterToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  queryPlaceholder: string;
  queryDisabled?: boolean;
  queryInputClassName?: string;
  querySectionClassName?: string;
  queryContainerClassName?: string;
  filtersClassName?: string;
  filtersPlacement?: 'separate' | 'inline';
  onSubmit?: () => void;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  submitLabel?: string;
  children?: ReactNode;
}

/**
 * Shared toolbar for search input + optional filter controls.
 * Keeps GHG search UIs consistent while letting each domain keep its own query behavior.
 */
export function SearchFilterToolbar({
  query,
  onQueryChange,
  queryPlaceholder,
  queryDisabled = false,
  queryInputClassName,
  querySectionClassName = 'flex flex-col gap-4 sm:flex-row sm:items-end',
  queryContainerClassName = 'flex flex-1 gap-2',
  filtersClassName = 'flex flex-wrap gap-3',
  filtersPlacement = 'separate',
  onSubmit,
  submitDisabled = false,
  submitLoading = false,
  submitLabel = 'Search',
  children,
}: SearchFilterToolbarProps) {
  const showSubmit = typeof onSubmit === 'function';

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !onSubmit) return;
    onSubmit();
  };

  return (
    <div className="space-y-4">
      <div className={querySectionClassName}>
        <div className={queryContainerClassName}>
          <Input
            placeholder={queryPlaceholder}
            value={query}
            onChange={(event) => {
              onQueryChange(event.target.value);
            }}
            onKeyDown={handleInputKeyDown}
            disabled={queryDisabled}
            className={queryInputClassName}
          />
          {showSubmit && (
            <Button onClick={onSubmit} disabled={submitDisabled || queryDisabled}>
              {submitLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              {submitLabel}
            </Button>
          )}
        </div>

        {filtersPlacement === 'inline' ? children : null}
      </div>

      {filtersPlacement === 'separate' && children ? (
        <div className={filtersClassName}>{children}</div>
      ) : null}
    </div>
  );
}
